import React, { FC } from 'react';
import AppliedFilterChip, {
  OverflowCountChip,
} from '@components/AppliedFilterChip';
import { APPLIED_FILTER_CHIP_GAP } from '@constants/appliedFilterChips';
import { Box, Stack } from '@mui/material';
import { useProjectDashboardActions } from '@store/zustand/project-dashboard-filters';
import { filtersPopoverOwnerProps } from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';
import useChipRowOverflow from '@views/Project/components/Toolbox/components/AppliedFilterChips/useChipRowOverflow';
import { useDashboardPartnersPopover } from '@views/Project/components/Toolbox/components/DashboardFilters/dashboardPartnersPopover.store';
import useDashboardPartnerFilter from '@views/Project/components/Toolbox/components/DashboardFilters/useDashboardPartnerFilter';

const gap = `${APPLIED_FILTER_CHIP_GAP}px`;

const noop = () => undefined;

const DashboardPartnerChips: FC = () => {
  const { selected, isAvailable } = useDashboardPartnerFilter();
  const { removePartner } = useProjectDashboardActions();
  const { open } = useDashboardPartnersPopover();
  const { containerRef, measureRowRef, visibleCount } = useChipRowOverflow(
    selected.length,
    selected.join('|'),
  );

  if (!isAvailable || selected.length === 0) {
    return null;
  }

  const visible = selected.slice(0, visibleCount);
  const hiddenCount = selected.length - visible.length;

  return (
    <Box
      ref={containerRef}
      flex={1}
      minWidth={0}
      position="relative"
      overflow="hidden"
    >
      <Stack
        ref={measureRowRef}
        aria-hidden
        direction="row"
        gap={gap}
        width="max-content"
        position="absolute"
        top={0}
        left={0}
        visibility="hidden"
        sx={{ pointerEvents: 'none' }}
      >
        {selected.map((partner) => (
          <AppliedFilterChip
            key={partner}
            summary={partner}
            onClick={noop}
            onClear={noop}
          />
        ))}
        <OverflowCountChip count={selected.length} />
      </Stack>

      <Stack
        data-testid="dashboard-partner-chips"
        direction="row"
        alignItems="center"
        flexWrap="nowrap"
        gap={gap}
        width="max-content"
        {...filtersPopoverOwnerProps}
      >
        {visible.map((partner) => (
          <AppliedFilterChip
            key={partner}
            summary={partner}
            onClick={(event) => open(event.currentTarget)}
            onClear={() => removePartner(partner)}
          />
        ))}

        {hiddenCount > 0 && (
          <OverflowCountChip
            count={hiddenCount}
            onClick={(event) => open(event.currentTarget)}
          />
        )}
      </Stack>
    </Box>
  );
};

export default DashboardPartnerChips;
